'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { useUserStore, type Address } from '@/store/user-store'
import { useCartStore } from '@/store/cart-store'
import { useFavoriteStore } from '@/store/favorite-store'
import { MEMBER_LEVELS } from '@/lib/constants'
import { formatPrice } from '@/lib/utils'
import {
  User, MapPin, Ticket, Clock, Settings, ChevronRight,
  Gift, Star, Wallet, Phone, Shield, Heart, LogOut, Loader2,
  Users, MessageCircle, X, Plus, Pencil, Trash2, Check,
  CalendarCheck, Crown, ShoppingBag, Bell, CreditCard, GiftIcon,
  Sparkles, UsersRound, RotateCcw, Zap, HeartHandshake
} from 'lucide-react'
import { toast } from 'sonner'

interface DbOrder {
  id: number; orderNo: string; status: string; paymentStatus: number
  items: any[]; actualPrice: number; paymentMethod: string
  deliveryAddress: string; receiverName: string; deliveryPhone: string
  remark: string; createdAt: string; auditStatus?: number
}

interface DbCoupon {
  id: number; title: string; type: string; value: number
  minSpend: number; startTime: string; endTime: string; isUsed: boolean
}

const STATUS_MAP: Record<number, string> = { 0: '待付款', 1: '制作中', 2: '配送中', 3: '已完成', 4: '已取消' }

export default function ProfilePage() {
  const { user, addresses, logout, removeAddress, setDefaultAddress, addAddress, updateAddress } = useUserStore()
  const cartItemCount = useCartStore(s => s.getItemCount())
  const favoriteCount = useFavoriteStore(s => s.favorites.length)

  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'active' | 'done'>('all')
  const [showInfo, setShowInfo] = useState(false)
  const [orders, setOrders] = useState<DbOrder[]>([])
  const [coupons, setCoupons] = useState<DbCoupon[]>([])
  const [loadingData, setLoadingData] = useState(true)
  const [showQR, setShowQR] = useState<'group' | 'manager' | null>(null)
  const [showAddressMgr, setShowAddressMgr] = useState(false)
  const [showAddAddr, setShowAddAddr] = useState(false)
  const [editAddr, setEditAddr] = useState<Address | null>(null)
  const [addrForm, setAddrForm] = useState({ province: '湖南省', city: '湘潭市', district: '岳塘区', detail: '', receiverName: '', phone: '', isDefault: false })

  const memberInfo = MEMBER_LEVELS.find(m => m.level === (user?.memberLevel || 1))
  const nextLevel = MEMBER_LEVELS.find(m => m.level === (user?.memberLevel || 1) + 1)

  useEffect(() => {
    if (!user) return
    Promise.all([
      fetch('/api/orders?userId=1').then(r => r.ok ? r.json() : []).catch(() => []),
      fetch('/api/coupons?userId=1').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([orderData, couponData]) => {
      setOrders(Array.isArray(orderData) ? orderData : (orderData.items || []))
      setCoupons(Array.isArray(couponData) ? couponData : [])
    }).finally(() => setLoadingData(false))
  }, [user])

  const filteredOrders = orders.filter(o => {
    const s = o.paymentStatus
    if (activeTab === 'all') return s !== 4
    if (activeTab === 'pending') return s === 0 || s === 1
    if (activeTab === 'active') return s === 2
    if (activeTab === 'done') return s === 3
    return true
  })
  const availableCoupons = coupons.filter(c => !c.isUsed)

  const handleLogout = () => { logout(); toast.success('已退出登录，将重新生成游客账号') }

  const openAddAddr = () => {
    setAddrForm({ province: '湖南省', city: '湘潭市', district: '岳塘区', detail: '', receiverName: '', phone: '', isDefault: false })
    setEditAddr(null); setShowAddAddr(true)
  }
  const openEditAddr = (addr: Address) => {
    setAddrForm({ province: addr.province, city: addr.city, district: addr.district, detail: addr.detail, receiverName: addr.receiverName, phone: addr.phone, isDefault: addr.isDefault })
    setEditAddr(addr); setShowAddAddr(true)
  }
  const handleSaveAddr = () => {
    if (!addrForm.detail.trim() || !addrForm.receiverName.trim() || !addrForm.phone.trim()) { toast.error('请填写完整地址信息'); return }
    if (editAddr) {
      updateAddress(editAddr.id, addrForm)
      toast.success('地址已更新')
    } else {
      addAddress(addrForm)
      toast.success('地址添加成功')
    }
    setShowAddAddr(false)
  }
  const handleDelAddr = (id: string) => {
    removeAddress(id); toast.success('地址已删除')
  }
  const handleSetDefault = (id: string) => {
    setDefaultAddress(id); toast.success('已设为默认')
  }

  if (!user) {
    return (
      <div className="min-h-screen pb-16 bg-gray-50">
        <TopBar />
        <div className="flex flex-col items-center pt-20"><Loader2 className="w-6 h-6 text-pink-500 animate-spin" /></div>
        <BottomNav />
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar />

      {/* 会员卡片 */}
      <Link href="/profile/edit" className="mx-4 mt-3 p-4 bg-gradient-to-r from-pink-500 to-rose-400 rounded-2xl text-white block active:scale-[0.98] transition-transform">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
            {user.avatar ? (
              <img src={user.avatar} alt="头像" className="w-full h-full object-cover" />
            ) : (
              <User className="w-7 h-7" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold">{user.nickname}</span>
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px]">{memberInfo?.name}</span>
            </div>
            <p className="text-[10px] opacity-80 mt-0.5">ID: {user.id}</p>
          </div>
          <div className="flex items-center gap-1 opacity-60">
            <span className="text-[10px]">编辑资料</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </Link>
      {nextLevel && (
        <div className="mx-4 mt-2">
          <div className="flex justify-between text-[10px] opacity-80">
            <span>成长值 {user.growth}</span>
            <span>距{nextLevel.name}还需 {nextLevel.growthNeeded - user.growth}</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full mt-1">
            <div className="h-full bg-white rounded-full" style={{ width: `${Math.min(100, (user.growth / nextLevel.growthNeeded) * 100)}%` }} />
          </div>
        </div>
      )}
      <div className="flex justify-around mx-4 mt-3 pt-3 border-t border-gray-100 bg-white rounded-b-xl">
        <Link href="/points" className="text-center">
          <div className="flex items-center justify-center gap-1"><Star className="w-3.5 h-3.5" /><span className="text-lg font-bold">{user.points}</span></div>
          <span className="text-[10px] text-gray-400">积分</span>
        </Link>
        <Link href="/balance" className="text-center">
          <div className="flex items-center justify-center gap-1"><Wallet className="w-3.5 h-3.5" /><span className="text-lg font-bold">¥{user.balance.toFixed(2)}</span></div>
          <span className="text-[10px] text-gray-400">余额</span>
        </Link>
        <Link href="/menu" className="text-center">
          <div className="flex items-center justify-center gap-1"><Gift className="w-3.5 h-3.5" /><span className="text-lg font-bold">{cartItemCount}</span></div>
          <span className="text-[10px] text-gray-400">购物车</span>
        </Link>
      </div>

      {/* 账号信息 */}
      {showInfo && (
        <div className="mx-4 mt-2 bg-white rounded-xl p-3 space-y-2 text-xs">
          <div className="flex justify-between text-gray-500"><span>游客账号ID</span><span className="font-mono text-gray-800">{user.id}</span></div>
          <div className="flex justify-between text-gray-500"><span>会员等级</span><span className="font-bold" style={{ color: memberInfo?.color }}>{memberInfo?.name} ({(memberInfo?.discount ?? 0.95) * 10}折)</span></div>
          <div className="flex justify-between text-gray-500"><span>手机号</span><span>{user.phone || '未绑定'}</span></div>
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-1 py-2 text-red-400 mt-2 border border-red-200 rounded-lg"><LogOut className="w-3.5 h-3.5" /> 退出登录</button>
        </div>
      )}

      {/* 我的订单 */}
      <div className="mx-4 mt-3 bg-white rounded-xl">
        <div className="flex items-center justify-between p-3 border-b border-gray-50">
          <span className="text-sm font-bold text-gray-800">我的订单</span>
          <span className="text-xs text-gray-400">{orders.length}笔订单</span>
        </div>
        <div className="flex border-b border-gray-100">
          {[
            { key: 'all', label: '全部' }, { key: 'pending', label: '待处理' },
            { key: 'active', label: '进行中' }, { key: 'done', label: '已完成' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'text-pink-500 border-pink-500' : 'text-gray-400 border-transparent'}`}
            >{tab.label}</button>
          ))}
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loadingData ? (
            <div className="p-6 text-center"><Loader2 className="w-4 h-4 text-pink-500 animate-spin mx-auto" /></div>
          ) : filteredOrders.length > 0 ? filteredOrders.map((order) => (
            <Link key={order.id} href={`/order/${order.orderNo}`} className="block p-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-gray-400 font-mono">{order.orderNo}</span>
                <div className="flex items-center gap-1">
                  {order.auditStatus === 1 && <span className="px-1 py-0.5 bg-amber-100 text-amber-600 text-[8px] rounded">待审核</span>}
                  <span className={`text-[10px] font-bold ${order.paymentStatus === 3 ? 'text-green-500' : order.paymentStatus === 2 ? 'text-blue-500' : order.paymentStatus === 4 ? 'text-gray-400' : 'text-amber-500'}`}>{STATUS_MAP[order.paymentStatus] || '未知'}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-1">
                {(order.items || []).slice(0, 3).map((item: any, idx: number) => (
                  <div key={idx} className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    <img src={item.image || '/images/cake-placeholder.jpg'} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-gray-400">{order.createdAt ? new Date(order.createdAt).toLocaleString('zh-CN') : ''}</span>
                <span className="text-sm font-bold text-gray-800">¥{order.actualPrice?.toFixed(2)}</span>
              </div>
            </Link>
          )) : (
            <div className="p-6 text-center text-xs text-gray-400">暂无订单</div>
          )}
        </div>
      </div>

      {/* 优惠券 */}
      <Link href="/coupons" className="mx-4 mt-3 bg-white rounded-xl p-3 block">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5"><Ticket className="w-4 h-4 text-pink-500" /><span className="text-sm font-bold text-gray-800">我的优惠券</span></div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400">{availableCoupons.length}张可用</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
          </div>
        </div>
        <div className="space-y-2">
          {loadingData ? (
            <div className="p-6 text-center"><Loader2 className="w-4 h-4 text-pink-500 animate-spin mx-auto" /></div>
          ) : availableCoupons.length > 0 ? availableCoupons.slice(0, 3).map(coupon => (
            <div key={coupon.id} className="flex items-center p-2.5 bg-pink-50 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-400 rounded-lg flex flex-col items-center justify-center text-white flex-shrink-0">
                {coupon.type === '满减' && <><span className="text-sm font-bold">¥{coupon.value}</span><span className="text-[7px]">满{coupon.minSpend}</span></>}
                {coupon.type === '折扣' && <><span className="text-sm font-bold">{coupon.value / 10}折</span><span className="text-[7px]">无门槛</span></>}
              </div>
              <div className="ml-2 flex-1">
                <p className="text-xs font-bold text-gray-700">{coupon.title}</p>
                <p className="text-[10px] text-gray-400">有效期至 {coupon.endTime?.split('T')[0]}</p>
              </div>
              <span className="px-2 py-1 bg-pink-500 text-white text-[10px] font-bold rounded">去使用</span>
            </div>
          )) : (
            <p className="text-xs text-gray-400 text-center py-3">暂无可用优惠券</p>
          )}
        </div>
      </Link>

      {/* 地址管理 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-pink-500" /><span className="text-sm font-bold text-gray-800">收货地址</span></div>
          <button onClick={() => setShowAddressMgr(true)} className="text-xs text-pink-500">管理</button>
        </div>
        {addresses.length > 0 ? addresses.slice(0, 2).map(addr => (
          <div key={addr.id} className="flex items-start gap-2 p-2 border-b border-gray-50 last:border-0">
            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-800">{addr.receiverName}</span>
                <span className="text-[10px] text-gray-400">{addr.phone}</span>
                {addr.isDefault && <span className="px-1 py-0.5 bg-pink-100 text-pink-500 text-[8px] rounded">默认</span>}
              </div>
              <p className="text-[10px] text-gray-400 truncate mt-0.5">{addr.province}{addr.city}{addr.district}{addr.detail}</p>
            </div>
          </div>
        )) : (
          <p className="text-xs text-gray-400 text-center py-3">暂无地址</p>
        )}
      </div>

      {/* 功能入口 */}
      <div className="mx-4 mt-3 bg-white rounded-xl overflow-hidden">
        {[
          { icon: Ticket, label: '我的优惠券', color: 'text-pink-500', badge: availableCoupons.length, href: '/coupons' },
          { icon: CalendarCheck, label: '每日签到', color: 'text-orange-500', href: '/checkin' },
          { icon: ShoppingBag, label: '积分商城', color: 'text-purple-500', href: '/points-mall' },
          { icon: GiftIcon, label: '礼品卡', color: 'text-amber-500', href: '/gift-card' },
          { icon: CreditCard, label: '兑换卡', color: 'text-indigo-500', href: '/redeem' },
          { icon: Heart, label: '我的收藏', color: 'text-red-500', badge: favoriteCount, href: '/favorites' },
          { icon: User, label: '个人信息', color: 'text-gray-500', href: '/profile/edit' },
          { icon: Crown, label: '会员中心', color: 'text-amber-500', href: '/member' },
          { icon: Bell, label: '通知中心', color: 'text-blue-500', href: '/notifications' },
          { icon: UsersRound, label: '社群福利', color: 'text-violet-500', href: '/community' },
          { icon: Phone, label: '联系店长', color: 'text-green-500', action: () => setShowQR('manager') },
          { icon: MessageCircle, label: '联系客服', color: 'text-blue-500', action: () => toast.info('客服微信：sweet_time_cs，工作时间 9:00-21:00') },
          { icon: RotateCcw, label: '退款/售后', color: 'text-orange-500', href: '/refund' },
          { icon: Star, label: '我的评价', color: 'text-amber-500', href: '/order/review' },
          { icon: HeartHandshake, label: '收礼人档案', color: 'text-rose-500', href: '/recipients' },
          { icon: Zap, label: '59分钟快送', color: 'text-orange-500', href: '/express' },
          { icon: Shield, label: '账号与安全', color: 'text-gray-500', href: '/settings' },
          { icon: Settings, label: '设置', color: 'text-gray-500', href: '/settings' },
        ].map(item => (
          item.href ? (
            <Link key={item.label} href={item.href} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-sm text-gray-700">{item.label}</span>
              </div>
              <div className="flex items-center gap-1">
                {item.badge ? <span className="px-1.5 py-0.5 bg-red-100 text-red-500 text-[10px] font-bold rounded-full">{item.badge}</span> : null}
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </div>
            </Link>
          ) : (
            <button key={item.label} onClick={item.action} className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 active:bg-gray-100 transition-colors">
              <div className="flex items-center gap-2">
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-sm text-gray-700">{item.label}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </button>
          )
        ))}
      </div>

      <div className="h-4" />
      <BottomNav />

      {/* ====== 地址管理弹框 ====== */}
      {showAddressMgr && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center">
          <div className="bg-white rounded-t-2xl w-full max-w-lg max-h-[80vh] p-4 pb-8 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">收货地址管理</h3>
              <button onClick={() => setShowAddressMgr(false)} className="p-1"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-2">
              {addresses.map(addr => (
                <div key={addr.id} className="p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{addr.receiverName}</span>
                      <span className="text-xs text-gray-400">{addr.phone}</span>
                      {addr.isDefault && <span className="px-1 py-0.5 bg-pink-500 text-white text-[8px] rounded">默认</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditAddr(addr)} className="p-1.5 text-gray-400 hover:text-pink-500"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelAddr(addr.id)} className="p-1.5 text-gray-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      {!addr.isDefault && <button onClick={() => handleSetDefault(addr.id)} className="px-2 py-1 text-[10px] text-pink-500 border border-pink-200 rounded">设为默认</button>}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{addr.province}{addr.city}{addr.district}{addr.detail}</p>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowAddressMgr(false); openAddAddr() }} className="w-full mt-3 py-3 bg-pink-500 text-white text-sm font-bold rounded-xl flex items-center justify-center gap-1.5 active:bg-pink-600">
              <Plus className="w-4 h-4" /> 新增收货地址
            </button>
          </div>
        </div>
      )}

      {/* ====== 新增/编辑地址弹框 ====== */}
      {showAddAddr && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold text-gray-800 mb-3">{editAddr ? '编辑地址' : '新增地址'}</h3>
            <div className="space-y-2">
              <input value={addrForm.receiverName} onChange={e => setAddrForm({ ...addrForm, receiverName: e.target.value })} placeholder="收货人姓名" className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none border border-gray-100 focus:border-pink-300" />
              <input value={addrForm.phone} onChange={e => setAddrForm({ ...addrForm, phone: e.target.value })} placeholder="手机号码" className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none border border-gray-100 focus:border-pink-300" />
              <select value={addrForm.district} onChange={e => setAddrForm({ ...addrForm, district: e.target.value })} className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none border border-gray-100">
                <option>岳塘区</option><option>雨湖区</option><option>湘潭县</option>
              </select>
              <input value={addrForm.detail} onChange={e => setAddrForm({ ...addrForm, detail: e.target.value })} placeholder="详细地址" className="w-full px-3 py-2 bg-gray-50 rounded-lg text-sm outline-none border border-gray-100 focus:border-pink-300" />
              <div className="flex items-center gap-2">
                <input type="checkbox" id="defAddr" checked={addrForm.isDefault} onChange={e => setAddrForm({ ...addrForm, isDefault: e.target.checked })} className="rounded" />
                <label htmlFor="defAddr" className="text-xs text-gray-500">设为默认地址</label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddAddr(false)} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600">取消</button>
              <button onClick={handleSaveAddr} className="flex-1 py-2 bg-pink-500 text-white rounded-lg text-sm font-bold">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== 二维码弹框 ====== */}
      {showQR && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4" onClick={() => setShowQR(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs text-center" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-3">{showQR === 'group' ? '会员社群' : '联系店长'}</h3>
            <div className="w-48 h-48 mx-auto bg-gray-100 rounded-xl flex flex-col items-center justify-center mb-3">
              <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-xs text-gray-400">二维码</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">长按识别或扫码添加</p>
            </div>
            <p className="text-xs text-gray-500 mb-4">{showQR === 'group' ? '加入会员社群，获取专属优惠和新品试吃机会' : '联系店长，获取专属定制服务和优惠'}</p>
            <button onClick={() => setShowQR(null)} className="w-full py-2 bg-gray-100 text-gray-600 text-sm rounded-full">关闭</button>
          </div>
        </div>
      )}
    </div>
  )
}
